import { ItemNoAndType } from "@coworker/apprestructured/src/shared/types/item";
import { GroupedSalesLocations } from "../../../types/SalesLocation";
import { useCallback, useMemo, memo } from "react";
import { useTranslation } from "@coworker/locales";
import { FixaSkeleton } from "@coworker/apprestructured/src/shared/wrappers/FixaSkeleton/FixaSkeleton";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import ModalColumn from "../../ModalColumn/ModalColumn";
import { ItemStockAndEds } from "@coworker/apprestructured/src/shared/components/item-cards/ItemStockAndEds/ItemStockAndEds";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { SalesLocation } from "@coworker/apprestructured/src/task-management/types/SalesLocation";
import styles from "./LocationsTab.module.css";

type LocationsTabProps = {
  itemNoAndType: ItemNoAndType | null;
  selectedSlid: string;
  onSlidChange: (location: string) => void;
  isLoadingLocations: boolean;
  filteredLocations: GroupedSalesLocations;
  totalAssignedSalesSpaceQuantity: number;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
};

const LocationsTab = memo(
  ({
    itemNoAndType,
    selectedSlid,
    onSlidChange,
    searchQuery,
    setSearchQuery,
    isLoadingLocations,
    filteredLocations: {
      homeAndPrimaryLocations,
      sgfLocations,
      otherSalesLocations,
    },
    totalAssignedSalesSpaceQuantity,
  }: LocationsTabProps) => {
    const { t } = useTranslation();

    const locationSection = useCallback(
      (locations: SalesLocation[], titleKey: string) => {
        if (isLoadingLocations) return <FixaSkeleton height="100px" />;
        if (locations.length === 0) return null;

        return (
          <>
            <FixaListViewItem
              title={t(titleKey)}
              quantityLabel={<span>SSQ</span>}
            />
            {locations.map((location) => (
              <FixaListViewItem
                id={`location-${location.slid}`}
                key={location.slid}
                title={location.specialityShopName}
                description={location.slid}
                emphasised
                quantityLabel={
                  <span>{location.assignedSalesSpaceQuantity}</span>
                }
                onChange={() => onSlidChange(location.slid)}
                control="radiobutton"
                controlProps={{
                  checked: location.slid === selectedSlid,
                  value: location.slid,
                }}
              />
            ))}
          </>
        );
      },
      [selectedSlid, isLoadingLocations, t, onSlidChange]
    );

    const mainLocationsContent = useMemo(
      () => locationSection(homeAndPrimaryLocations, "homeAndPrimaryString"),
      [homeAndPrimaryLocations, locationSection]
    );

    const otherLocationsContent = useMemo(
      () => locationSection(otherSalesLocations, "otherLocationsString"),
      [otherSalesLocations, locationSection]
    );

    const sgfLocationsContent = useMemo(
      () => locationSection(sgfLocations, "sgfLocationsString"),
      [sgfLocations, locationSection]
    );

    const hasAnyLocations =
      homeAndPrimaryLocations.length > 0 ||
      otherSalesLocations.length > 0 ||
      sgfLocations.length > 0;

    const totalCapacityContent = useMemo(() => {
      if (isLoadingLocations) return <FixaSkeleton height="50px" />;

      if (!hasAnyLocations) return null;

      return (
        <FixaListView id={"total-capacity"}>
          <FixaListViewItem
            className={styles["totalCapacity"]}
            title={`${t("totalCapacityString")} (ASSQ)`}
            quantityLabel={<span>{totalAssignedSalesSpaceQuantity}</span>}
            showDivider={false}
            inset
          />
        </FixaListView>
      );
    }, [
      isLoadingLocations,
      hasAnyLocations,
      totalAssignedSalesSpaceQuantity,
      t,
    ]);

    const hasSearchQuery = searchQuery.trim().length > 0;

    const noResultsContent = useMemo(() => {
      if (isLoadingLocations || hasAnyLocations) return null;

      return (
        <div className={styles["noResults"]}>
          <FixaText textStyle="Heading.L">{t("noTaskResultsString")}</FixaText>
          <FixaText textStyle="Body.L">
            {hasSearchQuery
              ? t("noResultsForString", { query: searchQuery })
              : t("noLocationsForArticleString")}
          </FixaText>
        </div>
      );
    }, [isLoadingLocations, hasAnyLocations, hasSearchQuery, searchQuery, t]);

    const disableSearch =
      isLoadingLocations ||
      itemNoAndType === null ||
      (!hasSearchQuery && !hasAnyLocations);

    const locationSearchContent = useMemo(() => {
      if (isLoadingLocations || (!hasAnyLocations && !hasSearchQuery))
        return null;
      return (
        <FixaSearch
          disabled={disableSearch ?? false}
          id="locations-search"
          onChange={(e) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery("")}
          placeholder={t("searchLocationString")}
          value={searchQuery}
        />
      );
    }, [
      isLoadingLocations,
      hasAnyLocations,
      hasSearchQuery,
      disableSearch,
      searchQuery,
      t,
      setSearchQuery,
    ]);

    if (itemNoAndType === null) {
      return (
        <ModalColumn gap="medium" className={styles["noLocations"]}>
          <FixaText textStyle="Heading.M">
            {t("noLocationsTitleString")}
          </FixaText>
          <FixaText data-testid="select-article-message" textStyle="Body.M">
            {t("selectArticleLocationString")}
          </FixaText>
        </ModalColumn>
      );
    }
    return (
      <ModalColumn gap="medium">
        {itemNoAndType && (
          <>
            <ItemStockAndEds
              itemNo={itemNoAndType?.no || ""}
              itemType={itemNoAndType?.type || ""}
            />
            {locationSearchContent}
            {totalCapacityContent}
            <FixaListView id="locations" className={styles["section"]}>
              {mainLocationsContent}
              {otherLocationsContent}
              {sgfLocationsContent}
            </FixaListView>
            {noResultsContent}
          </>
        )}
      </ModalColumn>
    );
  }
);

export default LocationsTab;
